/**
 * Données du questionnaire Big Five (personnalité)
 * 20 questions de tri forcé (ipsatif) réparties sur 4 pages de 5 questions.
 *
 * Principe : pour chaque question, le candidat doit classer 3 phrases
 * déclaratives de la plus représentative de lui (rang 1) à la moins
 * représentative (rang 3), via un drag-and-drop.
 *
 * Les 5 dimensions du Big Five évaluées :
 * - O : Ouverture à l'expérience (Openness)
 * - C : Conscienciosité (Conscientiousness)
 * - E : Extraversion
 * - A : Agréabilité (Agreeableness)
 * - N : Neuroticisme (Neuroticism) / Stabilité émotionnelle
 */

export const PAMI_QUESTIONS_PER_PAGE = 1;

export type PamiTrait = "O" | "C" | "E" | "A" | "N";

export interface PamiStatement {
  /** Identifiant unique de la phrase (utilisé comme valeur de tri) */
  id: string;

  prefixLabel: string; // "A. ", "B. ", "C. " (pour affichage devant la phrase)
  /** Texte de la phrase déclarative présentée au candidat */
  label: string;
}

export interface PamiQuestion {
  /** Identifiant unique de la question (utilisé comme clé de formulaire) */
  name: string;
  /** Les 3 phrases à classer par ordre de préférence */
  statements: [PamiStatement, PamiStatement, PamiStatement];
}

/**
 * Consigne affichée en tête du questionnaire / de chaque question.
 * Toutes les questions partagent la même consigne : pas de label individuel.
 */
export const pamiInstructions =
  "Classez les 3 propositions de celle qui vous correspond le plus (première position) à celle qui vous correspond le moins (dernière position). Répondez de manière spontanée et sincère, il n'y a pas de bonnes ou de mauvaises réponses.";

export const pamiQuestions: PamiQuestion[] = [
  {
    name: "q1",
    statements: [
      {
        id: "Statut4",
        prefixLabel: "A. ",
        label:
          "Pouvoir participer à des événements importants (conférences, salons).",
      },
      {
        id: "O1i6",
        prefixLabel: "B. ",
        label: "Aimer analyser des problèmes sous plusieurs angles.",
      },
      {
        id: "O2i6",
        prefixLabel: "C. ",
        label: "Trouver facilement des idées originales.",
      },
    ],
  },
  {
    name: "q2",
    statements: [
      {
        id: "Auto1",
        prefixLabel: "A. ",
        label:
          "Pouvoir utiliser mes propres méthodes pour atteindre les objectifs.",
      },
      {
        id: "Secu1",
        prefixLabel: "B. ",
        label: "Vouloir un contrat de travail pérenne.",
      },
      {
        id: "H2i5",
        prefixLabel: "C. ",
        label:
          "Préférer perdre une opportunité plutôt que de compromettre mes principes.",
      },
    ],
  },
  {
    name: "q3",
    statements: [
      {
        id: "O1i2",
        prefixLabel: "A. ",
        label: "Apprécier les activités qui stimulent l'esprit.",
      },
      {
        id: "C2i4",
        prefixLabel: "B. ",
        label: "Livrer un travail de qualité.",
      },
      {
        id: "Manag4",
        prefixLabel: "C. ",
        label:
          "Pouvoir bénéficier d'un leadership bienveillant.",
      },
    ],
  },
  {
    name: "q4",
    statements: [
      {
        id: "H1i4",
        prefixLabel: "A. ",
        label:
          "Etre honnête plutôt que de manipuler pour obtenir ce que je veux.",
      },
      {
        id: "Statut5",
        prefixLabel: "B. ",
        label:
          "Pouvoir gagner en visibilité grâce à mon titre professionnel.",
      },
      {
        id: "C2i3",
        prefixLabel: "C. ",
        label:
          "Réaliser consciencieusement le travail qui m'est confié.",
      },
    ],
  },
  {
    name: "q5",
    statements: [
      {
        id: "Equi2",
        prefixLabel: "A. ",
        label: "Pouvoir télétravailler.",
      },
      {
        id: "E2i8",
        prefixLabel: "B. ",
        label: "Apprécier être entouré(e) de monde.",
      },
      {
        id: "Auto6",
        prefixLabel: "C. ",
        label: "Pouvoir être libre dans la réalisation de mon travail.",
      },
    ],
  },
  {
    name: "q6",
    statements: [
      {
        id: "H1i7",
        prefixLabel: "A. ",
        label:
          "Ne pas dire ce que l'on attend de moi juste pour faire bonne impression.",
      },
      {
        id: "H2i7",
        prefixLabel: "B. ",
        label: "Agir de façon honnête en toutes circonstances.",
      },
      {
        id: "C1i2",
        prefixLabel: "C. ",
        label: "Terminer un projet que j'ai commencé.",
      },
    ],
  },
  {
    name: "q7",
    statements: [
      {
        id: "Rev4",
        prefixLabel: "A. ",
        label:
            "Vouloir être rémunéré(e) pour des missions exceptionnelles ou des heures supplémentaires.",
      },
      {
        id: "O1i5",
        prefixLabel: "B. ",
        label: "Aimer lire des ouvrages qui me font réfléchir.",
      },
      {
        id: "Manag2",
        prefixLabel: "C. ",
        label:
          "Vouloir des retours réguliers et constructifs sur mon travail.",
      },
    ],
  },
  {
    name: "q8",
    statements: [
      {
        id: "E2i2",
        prefixLabel: "A. ",
        label:
          "Etre à l'aise dans des événements qui rassemblent beaucoup de monde.",
      },
      {
        id: "O2i7",
        prefixLabel: "B. ",
        label: "Avoir besoin d'exprimer ma créativité.",
      },
      {
        id: "N1i1",
        prefixLabel: "C. ",
        label: "Etre détendu(e) la plupart du temps.",
      },
    ],
  },
  {
    name: "q9",
    statements: [
      {
        id: "E2i3",
        prefixLabel: "A. ",
        label: "Avoir des gens autour de moi plutôt qu'être seul(e).",
      },
      {
        id: "N2i5",
        prefixLabel: "B. ",
        label:
          "Ne pas me laisser submerger par mes émotions dans les situations difficiles.",
      },
      {
        id: "H2i1",
        prefixLabel: "C. ",
        label:
          "Refuser de tirer profit d'une situation même si personne ne le découvrirait.",
      },
    ],
  },
  {
    name: "q10",
    statements: [
      {
        id: "Var1",
        prefixLabel: "A. ",
        label:
          "Vouloir travailler sur des projets différents.",
      },
      {
        id: "H1i1",
        prefixLabel: "B. ",
        label:
          "Etre authentique et ne pas tromper les autres.",
      },
      {
        id: "H2i4",
        prefixLabel: "C. ",
        label:
          "Respecter les règles, même quand je pourrais les contourner sans risque.",
      },
    ],
  },
  {
    name: "q11",
    statements: [
      {
        id: "E1i2",
        prefixLabel: "A. ",
        label: "Diriger les choses quand je fais partie d'un groupe.",
      },
      {
        id: "A2i3",
        prefixLabel: "B. ",
        label: "Etre toujours disponible pour apporter mon aide.",
      },
      {
        id: "E2i7",
        prefixLabel: "C. ",
        label:
          "Engager la conversation avec des personnes que je ne connais pas.",
      },
    ],
  },
  {
    name: "q12",
    statements: [
      {
        id: "Equi4",
        prefixLabel: "A. ",
        label:
          "Pouvoir avoir du temps pour mes loisirs en dehors du travail.",
      },
      {
        id: "N1i4",
        prefixLabel: "B. ",
        label: "Ne pas avoir tendance à me faire du souci.",
      },
      {
        id: "C2i2",
        prefixLabel: "C. ",
        label: "Suivre attentivement les consignes qu'on me donne.",
      },
    ],
  },
  {
    name: "q13",
    statements: [
      {
        id: "Secu5",
        prefixLabel: "A. ",
        label: "Vouloir un poste pérenne dans mon entreprise/organisation.",
      },
      {
        id: "N2i3",
        prefixLabel: "B. ",
        label: "Garder mon sang-froid dans les situations difficiles.",
      },
      {
        id: "A1i5",
        prefixLabel: "C. ",
        label: "Privilégier le dialogue aux rapports de force.",
      },
    ],
  },
  {
    name: "q14",
    statements: [
      {
        id: "E1i3",
        prefixLabel: "A. ",
        label: "Animer les réunions et mener les discussions.",
      },
      {
        id: "O2i2",
        prefixLabel: "B. ",
        label: "Aimer laisser libre cours à mon imagination.",
      },
      {
        id: "H1i5",
        prefixLabel: "C. ",
        label: "Eviter de mentir, même quand cela me servirait.",
      },
    ],
  },
  {
    name: "q15",
    statements: [
      {
        id: "Rea1",
        prefixLabel: "A. ",
        label: "Pouvoir voir les effets de mon travail.",
      },
      {
        id: "E1i1",
        prefixLabel: "B. ",
        label: "M'affirmer sans hésitation.",
      },
      {
        id: "O2i4",
        prefixLabel: "C. ",
        label: "Prendre plaisir à imaginer des choses nouvelles.",
      },
    ],
  },
  {
    name: "q16",
    statements: [
      {
        id: "Equi1",
        prefixLabel: "A. ",
        label:
          "Pouvoir ajuster mes horaires en fonction de mes obligations personnelles.",
      },
      {
        id: "Manag8",
        prefixLabel: "B. ",
        label:
          "Pouvoir être reconnu(e) et respecté(e) dans mon travail.",
      },
      {
        id: "E2i5",
        prefixLabel: "C. ",
        label: "Aller facilement vers les autres.",
      },
    ],
  },
  {
    name: "q17",
    statements: [
      {
        id: "Rev5",
        prefixLabel: "A. ",
        label:
          "Vouloir des primes liées à mes performances individuelles.",
      },
      {
        id: "Rea8",
        prefixLabel: "B. ",
        label: "Pouvoir me sentir accompli(e) dans mon travail.",
      },
      {
        id: "Secu6",
        prefixLabel: "C. ",
        label:
          "Vouloir travailler dans une entreprise financièrement stable.",
      },
    ],
  },
  {
    name: "q18",
    statements: [
      {
        id: "Rev6",
        prefixLabel: "A. ",
        label: "Vouloir des augmentations de salaire.",
      },
      {
        id: "A2i5",
        prefixLabel: "B. ",
        label: "Etre sensible aux besoins des autres.",
      },
      {
        id: "C1i4",
        prefixLabel: "C. ",
        label: "Respecter les échéances que je me fixe.",
      },
    ],
  },
  {
    name: "q19",
    statements: [
      {
        id: "O1i7",
        prefixLabel: "A. ",
        label:
          "Apprécier les discussions profondes et argumentées.",
      },
      {
        id: "N2i7",
        prefixLabel: "B. ",
        label: "Parvenir à prendre du recul quand je suis stressé(e).",
      },
      {
        id: "C2i5",
        prefixLabel: "C. ",
        label: "Respecter les délais qui me sont fixés.",
      },
    ],
  },
  {
    name: "q20",
    statements: [
      {
        id: "Auto7",
        prefixLabel: "A. ",
        label:
          "Pouvoir organiser mon travail comme je l'entends.",
      },
      {
        id: "Equi8",
        prefixLabel: "B. ",
        label:
          "Pouvoir ne pas sacrifier ma vie personnelle au profit du travail.",
      },
      {
        id: "Rea2",
        prefixLabel: "C. ",
        label:
          "Vouloir que la qualité de mon travail soit reconnue.",
      },
    ],
  },
  {
    name: "q21",
    statements: [
      {
        id: "N1i7",
        prefixLabel: "A. ",
        label: "Etre serein(e) la plupart du temps.",
      },
      {
        id: "Auto4",
        prefixLabel: "B. ",
        label:
          "Pouvoir prendre des initiatives sans demander d'autorisation.",
      },
      {
        id: "O1i8",
        prefixLabel: "C. ",
        label: "Etre stimulé(e) par les défis intellectuels.",
      },
    ],
  },
  {
    name: "q22",
    statements: [
      {
        id: "Auto5",
        prefixLabel: "A. ",
        label: "Pouvoir prendre mes propres décisions.",
      },
      {
        id: "O1i3",
        prefixLabel: "B. ",
        label: "Avoir une grande curiosité intellectuelle.",
      },
      {
        id: "E2i4",
        prefixLabel: "C. ",
        label: "Etre rapidement à l'aise dans un groupe.",
      },
    ],
  },
  {
    name: "q23",
    statements: [
      {
        id: "Rev1",
        prefixLabel: "A. ",
        label:
          "Vouloir un salaire aligné sur mes compétences et mon expérience.",
      },
      {
        id: "H1i2",
        prefixLabel: "B. ",
        label: "Rester sincère, même si cela me coûte un avantage.",
      },
      {
        id: "C2i7",
        prefixLabel: "C. ",
        label: "Tenir mes engagements professionnels.",
      },
    ],
  },
  {
    name: "q24",
    statements: [
      {
        id: "Var2",
        prefixLabel: "A. ",
        label: "Vouloir alterner entre plusieurs types de tâches au travail.",
      },
      {
        id: "Manag3",
        prefixLabel: "B. ",
        label:
          "Pouvoir discuter ouvertement de mes difficultés avec mon supérieur.",
      },
      {
        id: "N2i6",
        prefixLabel: "C. ",
        label: "Garder une attitude posée, même sous stress.",
      },
    ],
  },
  {
    name: "q25",
    statements: [
      {
        id: "E1i8",
        prefixLabel: "A. ",
        label: "Faire entendre ma voix.",
      },
      {
        id: "A2i4",
        prefixLabel: "B. ",
        label:
          "Prendre volontiers du temps pour aider quelqu'un en difficulté.",
      },
      {
        id: "Manag1",
        prefixLabel: "C. ",
        label:
          "Vouloir un manager qui valorise mon développement professionnel.",
      },
    ],
  },
  {
    name: "q26",
    statements: [
      {
        id: "Rea4",
        prefixLabel: "A. ",
        label: "Pouvoir valoriser mes compétences au travail.",
      },
      {
        id: "A1i4",
        prefixLabel: "B. ",
        label: "Faire de mon mieux pour désamorcer les conflits.",
      },
      {
        id: "N2i2",
        prefixLabel: "C. ",
        label:
          "Prendre les bonnes décisions en situation de stress.",
      },
    ],
  },
  {
    name: "q27",
    statements: [
      {
        id: "H1i3",
        prefixLabel: "A. ",
        label:
          "Ne pas chercher à séduire quelqu'un dans mon intérêt personnel.",
      },
      {
        id: "Auto8",
        prefixLabel: "B. ",
        label:
          "Pouvoir travailler sans être constamment encadré(e).",
      },
      {
        id: "Rea5",
        prefixLabel: "C. ",
        label:
          "Pouvoir atteindre des objectifs professionnels ambitieux.",
      },
    ],
  },
  {
    name: "q28",
    statements: [
      {
        id: "Secu7",
        prefixLabel: "A. ",
        label:
          "Préférer éviter les situations professionnelles trop risquées ou instables.",
      },
      {
        id: "Var3",
        prefixLabel: "B. ",
        label: "Vouloir ne pas toujours faire la même chose au travail.",
      },
      {
        id: "C1i6",
        prefixLabel: "C. ",
        label: "Me fixer des priorités claires et m'y tenir.",
      },
    ],
  },
  {
    name: "q29",
    statements: [
      {
        id: "Statut6",
        prefixLabel: "A. ",
        label:
          "Pouvoir accéder à un réseau professionnel important.",
      },
      {
        id: "Manag7",
        prefixLabel: "B. ",
        label:
          "Pouvoir travailler en me sentant soutenu(e) par ma hiérarchie.",
      },
      {
        id: "A1i3",
        prefixLabel: "C. ",
        label:
          "Coopérer avec les autres plutôt qu'être en conflit avec eux.",
      },
    ],
  },
  {
    name: "q30",
    statements: [
      {
        id: "Var7",
        prefixLabel: "A. ",
        label:
          "Vouloir des journées de travail qui ne se ressemblent pas.",
      },
      {
        id: "H2i8",
        prefixLabel: "B. ",
        label: "Faire ce qui est juste.",
      },
      {
        id: "Rea3",
        prefixLabel: "C. ",
        label:
          "Ressentir une fierté professionnelle à la fin d'une mission ou d'un projet.",
      },
    ],
  },
  {
    name: "q31",
    statements: [
      {
        id: "Statut7",
        prefixLabel: "A. ",
        label:
          "Vouloir un poste qui suscite une certaine reconnaissance sociale.",
      },
      {
        id: "O2i8",
        prefixLabel: "B. ",
        label: "Aimer créer des choses.",
      },
      {
        id: "H2i6",
        prefixLabel: "C. ",
        label: "Me comporter de manière intègre.",
      },
    ],
  },
  {
    name: "q32",
    statements: [
      {
        id: "Secu8",
        prefixLabel: "A. ",
        label:
          "Vouloir un emploi stable.",
      },
      {
        id: "Equi3",
        prefixLabel: "B. ",
        label: "Pouvoir bénéficier de jours de congés flexibles.",
      },
      {
        id: "N1i2",
        prefixLabel: "C. ",
        label: "Etre rarement nerveux(se).",
      },
    ],
  },
  {
    name: "q33",
    statements: [
      {
        id: "Rev2",
        prefixLabel: "A. ",
        label:
          "Pouvoir financer mon style de vie idéal grâce à mon travail.",
      },
      {
        id: "E1i4",
        prefixLabel: "B. ",
        label: "Prendre volontiers la parole dans un groupe.",
      },
      {
        id: "Equi5",
        prefixLabel: "C. ",
        label:
          "Pouvoir déconnecter en dehors des heures de bureau.",
      },
    ],
  },
  {
    name: "q34",
    statements: [
      {
        id: "E1i7",
        prefixLabel: "A. ",
        label: "Défendre mon point de vue avec assurance.",
      },
      {
        id: "O2i1",
        prefixLabel: "B. ",
        label: "Aimer explorer des idées innovantes.",
      },
      {
        id: "N2i4",
        prefixLabel: "C. ",
        label:
          "Gérer le stress sans me laisser déstabiliser.",
      },
    ],
  },
  {
    name: "q35",
    statements: [
      {
        id: "Rev7",
        prefixLabel: "A. ",
        label: "Vouloir des perspectives d'évolution salariale.",
      },
      {
        id: "Var5",
        prefixLabel: "B. ",
        label: "Vouloir des missions diverses et variées.",
      },
      {
        id: "C2i1",
        prefixLabel: "C. ",
        label:
          "Faire mon travail même quand je n'en ai pas envie.",
      },
    ],
  },
  {
    name: "q36",
    statements: [
      {
        id: "Secu4",
        prefixLabel: "A. ",
        label: "Vouloir un revenu régulier.",
      },
      {
        id: "C1i8",
        prefixLabel: "B. ",
        label: "Poursuivre mes objectifs sans me laisser distraire.",
      },
      {
        id: "N1i5",
        prefixLabel: "C. ",
        label: "Ne pas avoir tendance à m'inquiéter pour de petites choses.",
      },
    ],
  },
  {
    name: "q37",
    statements: [
      {
        id: "A2i7",
        prefixLabel: "A. ",
        label: "Me rendre disponible pour aider les autres.",
      },
      {
        id: "Var6",
        prefixLabel: "B. ",
        label:
          "Vouloir de la variété dans mes activités professionnelles.",
      },
      {
        id: "A1i8",
        prefixLabel: "C. ",
        label: "Faire preuve de diplomatie dans les situations tendues.",
      },
    ],
  },
  {
    name: "q38",
    statements: [
      {
        id: "A2i6",
        prefixLabel: "A. ",
        label:
          "Faire passer les besoins des autres avant les miens quand c'est nécessaire.",
      },
      {
        id: "N1i3",
        prefixLabel: "B. ",
        label: "Etre rarement inquiet(e) ou anxieux(se).",
      },
      {
        id: "A1i1",
        prefixLabel: "C. ",
        label: "Eviter d'être en conflit avec les autres.",
      },
    ],
  },
  {
    name: "q39",
    statements: [
      {
        id: "Statut1",
        prefixLabel: "A. ",
        label: "Vouloir travailler pour une entreprise prestigieuse.",
      },
      {
        id: "C1i3",
        prefixLabel: "B. ",
        label: "Etre constant(e) dans mon travail, même sans supervision.",
      },
      {
        id: "A1i6",
        prefixLabel: "C. ",
        label:
          "Chercher à comprendre le point de vue de l'autre en cas de désaccord.",
      },
    ],
  },
  {
    name: "q40",
    statements: [
      {
        id: "Statut3",
        prefixLabel: "A. ",
        label: "Pouvoir mentionner mon employeur avec fierté.",
      },
      {
        id: "A2i8",
        prefixLabel: "B. ",
        label: "Prêter main-forte sans qu'on me le demande.",
      },
      {
        id: "C1i5",
        prefixLabel: "C. ",
        label: "Aller au bout de ce que j'entreprends.",
      },
    ],
  },
];
